import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Close from '@mui/icons-material/Close';
import StopCircleOutlined from '@mui/icons-material/StopCircleOutlined';
import { getOrangeActionButtonSx } from './buttonStyles';

function AccountMenuHeader({
	isMobile,
	isImpersonating,
	theme,
	t,
	currentUser = undefined,
	onEndImpersonation,
	onCloseMenu,
}) {
	const firstInitial = currentUser?.firstName?.charAt(0)?.toUpperCase() || '';
	const lastInitial = currentUser?.lastName?.charAt(0)?.toUpperCase() || '';
	const headerLabelKey = isImpersonating
		? 'impersonating_header_label'
		: 'header_label';

	const styles = {
		headerContainer: {
			borderBottom: `1px solid ${theme.palette.base.border}`,
			backgroundColor: isMobile ? undefined : theme.palette.base.light,
		},
		contentRow: {
			display: 'flex',
			alignItems: 'flex-start',
			columnGap: 1.5,
		},
		avatar: {
			bgcolor: theme.palette.primary.main,
			color: '#FFFFFF',
			width: theme.spacing(4.5),
			height: theme.spacing(4.5),
			fontSize: theme.palette.textSize.sizeSm,
			fontWeight: theme.palette.fontWeight.regular,
			mt: 0.25,
		},
		headerLabel: {
			color: theme.palette.muted.main,
			fontSize: theme.spacing(2),
			fontWeight: 400,
		},
		userName: {
			lineHeight: 1.2,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		userEmail: {
			color: theme.palette.muted.main,
			fontSize: theme.spacing(1.75),
			fontWeight: 400,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		endButton: {
			alignSelf: 'flex-start',
			...getOrangeActionButtonSx(theme),
		},
		closeButton: { mt: -0.5, mr: -1 },
	};

	return (
		<Box
			aria-label="text"
			role="menuitem"
			tabIndex={0}
			paddingTop={2}
			paddingBottom={2}
			sx={styles.headerContainer}
		>
			<Box
				aria-label="text"
				role="menuitem"
				tabIndex={0}
				paddingLeft={2}
				paddingRight={2}
				sx={styles.contentRow}
			>
				<Avatar aria-label="user initials" sx={styles.avatar}>
					{firstInitial}
					{lastInitial}
				</Avatar>
				<Box flex={1} minWidth={0} tabIndex={isMobile ? undefined : 0}>
					<Typography
						aria-label="header label"
						role="menuitem"
						tabIndex={0}
						sx={styles.headerLabel}
					>
						{t(headerLabelKey)}
					</Typography>
					<Typography
						aria-label="user full name"
						tabIndex={0}
						role="paragraph"
						variant="subtitle1"
						className={
							isMobile ? 'userDropdown-mobile' : 'userDropdown-desktop'
						}
						sx={styles.userName}
					>
						{currentUser?.firstName} {currentUser?.lastName}
					</Typography>
					<Typography
						className={
							isMobile ? 'userDropdown-mobile' : 'userDropdown-desktop'
						}
						aria-label={isMobile ? undefined : 'user email'}
						tabIndex={isMobile ? undefined : 0}
						role={isMobile ? undefined : 'paragraph'}
						sx={styles.userEmail}
					>
						{currentUser?.email}
					</Typography>
					{isImpersonating && (
						<Box pt={1}>
							<Button
								variant="contained"
								color="inherit"
								size="medium"
								startIcon={<StopCircleOutlined fontSize="small" />}
								role="button"
								data-testid={t('impersonate:end_impersonation')}
								aria-label={t('impersonate:end_impersonation')}
								onClick={onEndImpersonation}
								sx={styles.endButton}
							>
								{t('impersonate:end_impersonation')}
							</Button>
						</Box>
					)}
				</Box>
				<IconButton
					aria-label="clear icon button"
					aria-hidden="false"
					role="button"
					disableRipple
					onClick={onCloseMenu}
					sx={styles.closeButton}
				>
					<Close
						fontSize="small"
						role="graphics-symbol"
						aria-hidden="true"
						aria-label="clear icon"
					/>
				</IconButton>
			</Box>
		</Box>
	);
}

AccountMenuHeader.propTypes = {
	isMobile: PropTypes.bool.isRequired,
	isImpersonating: PropTypes.bool.isRequired,
	theme: PropTypes.object.isRequired,
	t: PropTypes.func.isRequired,
	currentUser: PropTypes.object,
	onEndImpersonation: PropTypes.func.isRequired,
	onCloseMenu: PropTypes.func.isRequired,
};

export default AccountMenuHeader;
